import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Send, Copy, Unlink, RefreshCw, BookMarked, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SECTORES = [
  ["sector_encurtidos", "Encurtidos"], ["sector_conservas", "Conservas"], ["sector_carnicos", "Cárnicos"],
  ["sector_lacteos", "Lácteos"], ["sector_panaderia", "Panadería"], ["sector_generico", "Genérico"],
];

function NormaCard({ tenant, onChanged }) {
  const [normas, setNormas] = useState([]);
  const [sel, setSel] = useState(tenant?.norma_activa || "IFS_V8");
  const [saving, setSaving] = useState(false);
  const actual = tenant?.norma_activa || "IFS_V8";

  useEffect(() => { api.get("/normas").then((r) => setNormas(r.data)).catch(() => {}); }, []);

  const cambiar = async () => {
    setSaving(true);
    try {
      await api.patch(`/tenants/${tenant.id}/norma`, { norma_activa: sel });
      toast.success("Norma cambiada. Readiness recalculado.");
      onChanged?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const seleccionada = normas.find((n) => n.codigo === sel);

  return (
    <Card className="p-6 border-[#E2E8F0] max-w-lg" data-testid="norma-card">
      <div className="flex items-center gap-2 mb-1">
        <BookMarked size={18} className="text-[#FF5625]" />
        <span className="font-black tracking-tight text-[#0E2A47]">Norma activa</span>
      </div>
      <p className="text-sm text-[#0E2A47]/60 mb-3">
        Determina el marco normativo de tu empresa. Al cambiar de norma se <b>conservan tus documentos y mapeos</b>; el readiness se recalcula contra el catálogo de la nueva norma.
      </p>
      <Select value={sel} onValueChange={setSel}>
        <SelectTrigger data-testid="norma-select" className="w-72"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {normas.map((n) => <SelectItem key={n.codigo} value={n.codigo}>{n.nombre_corto || n.nombre}</SelectItem>)}
        </SelectContent>
      </Select>
      {seleccionada?.descripcion_corta && (
        <p className="text-xs text-[#0E2A47]/50 mt-2">{seleccionada.descripcion_corta}</p>
      )}
      {sel !== actual && (
        <div className="mt-4 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3" data-testid="norma-aviso">
          <div className="flex items-start gap-2 text-sm text-[#92400E]">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Vas a cambiar la norma activa. La numeración de requisitos difiere entre normas, por lo que algunos mapeos existentes podrían dejar de coincidir y tendrás que revisarlos. Tus documentos no se borran.</span>
          </div>
          <Button onClick={cambiar} disabled={saving} data-testid="norma-confirmar"
            className="mt-3 bg-[#FF5625] hover:bg-[#E0451A]">
            {saving ? "Cambiando…" : "Confirmar cambio de norma"}
          </Button>
        </div>
      )}
    </Card>
  );
}

function TelegramCard() {
  const [estado, setEstado] = useState(null);
  const [codigo, setCodigo] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = () => api.get("/cuenta/telegram").then((r) => setEstado(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const generar = async () => {
    setCargando(true);
    try {
      const { data } = await api.post("/cuenta/telegram/codigo");
      setCodigo(data.codigo);
      toast.success("Código generado (válido 15 minutos)");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setCargando(false);
  };

  const desvincular = async (chatId) => {
    try { await api.delete(`/cuenta/telegram/${chatId}`); toast.success("Chat desvinculado"); cargar(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-6 border-[#E2E8F0] max-w-lg" data-testid="telegram-card">
      <div className="flex items-center gap-2 mb-1">
        <Send size={18} className="text-[#FF5625]" />
        <span className="font-black tracking-tight text-[#0E2A47]">Bot de Telegram</span>
      </div>
      <p className="text-sm text-[#0E2A47]/60 mb-4">
        Vincula tu cuenta a <b>@Auditready_bot</b> para registrar controles, consultar lotes y recibir avisos (no conformidades, lotes bloqueados, gaps KO y recordatorios).
      </p>

      {estado?.vinculado && (
        <div className="mb-4 space-y-2" data-testid="telegram-vinculados">
          {estado.chats.map((c) => (
            <div key={c.chat_id} className="flex items-center justify-between bg-[#F7F8FA] rounded-md px-3 py-2 text-sm">
              <span className="text-[#0E2A47]">✅ Vinculado{c.tg_username ? ` · @${c.tg_username}` : ""}</span>
              <button onClick={() => desvincular(c.chat_id)} data-testid={`telegram-desvincular-${c.chat_id}`}
                className="text-[#0E2A47]/50 hover:text-red-600 flex items-center gap-1 text-xs"><Unlink size={13} />Desvincular</button>
            </div>
          ))}
        </div>
      )}

      {codigo ? (
        <div className="rounded-lg border-2 border-dashed border-[#FF5625]/40 bg-[#FF5625]/5 p-4 text-center" data-testid="telegram-codigo">
          <div className="text-xs uppercase tracking-wider text-[#0E2A47]/50 mb-1">Tu código (válido 15 min)</div>
          <div className="text-3xl font-black tracking-[0.3em] text-[#0E2A47] flex items-center justify-center gap-3">
            {codigo}
            <button onClick={() => { navigator.clipboard?.writeText(codigo); toast.success("Copiado"); }} data-testid="telegram-copiar" className="text-[#0E2A47]/40 hover:text-[#FF5625]"><Copy size={18} /></button>
          </div>
          <div className="text-sm text-[#0E2A47]/70 mt-3">
            Abre <b>@Auditready_bot</b> en Telegram y envía:<br />
            <code className="bg-white px-2 py-0.5 rounded border border-[#E2E8F0]">/vincular {codigo}</code>
          </div>
          <Button variant="ghost" size="sm" onClick={generar} className="mt-3 text-[#0E2A47]/60"><RefreshCw size={13} className="mr-1" />Generar otro</Button>
        </div>
      ) : (
        <Button onClick={generar} disabled={cargando} data-testid="telegram-generar" className="bg-[#0E2A47] hover:bg-[#163C65]">
          <Send size={15} className="mr-1" />{estado?.vinculado ? "Vincular otro dispositivo" : "Generar código de vinculación"}
        </Button>
      )}
    </Card>
  );
}

function EmpresaCard({ tenant, onSaved }) {
  const [nombre, setNombre] = useState(tenant?.nombre || "");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!nombre.trim()) return toast.error("El nombre no puede estar vacío");
    setSaving(true);
    try {
      await api.patch(`/tenants/${tenant.id}/nombre`, { nombre: nombre.trim() });
      toast.success("Nombre de empresa actualizado");
      onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Card className="p-6 border-[#E2E8F0] max-w-lg" data-testid="empresa-card">
      <div className="flex items-center gap-2 mb-1">
        <BookMarked size={18} className="text-[#FF5625]" />
        <span className="font-black tracking-tight text-[#0E2A47]">Datos de la empresa</span>
      </div>
      <p className="text-sm text-[#0E2A47]/60 mb-3">
        Este nombre encabeza el <b>Modo Inspector</b> y el <b>dossier de auditoría</b>. Asegúrate de que sea el nombre legal real de tu empresa.
      </p>
      <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Nombre de la empresa</Label>
      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="empresa-nombre" className="mt-1.5 w-72" placeholder="Cárnicas del Valle S.L." />
      <div className="mt-4">
        <Button onClick={guardar} disabled={saving} data-testid="empresa-guardar" className="bg-[#0E2A47] hover:bg-[#163C65]">
          <Save size={15} className="mr-1" />{saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </Card>
  );
}

export default function Ajustes() {
  const { user, tenant, reload } = useAuth();
  const [sector, setSector] = useState(tenant?.sector_doctrina || "sector_generico");
  const esAdmin = ["superadmin", "admin"].includes(user?.rol);

  const guardar = async () => {
    try {
      await api.patch(`/tenants/${user.tenant_id}/sector`, { sector_doctrina: sector });
      toast.success("Sector de doctrina actualizado");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="ajustes-page" className="space-y-6">
      <PageHeader overline="Configuración" title="Ajustes" />

      {esAdmin && tenant && <EmpresaCard tenant={tenant} onSaved={reload} />}

      <TelegramCard />

      {esAdmin && tenant && <NormaCard tenant={tenant} onChanged={reload} />}

      {esAdmin && (
        <Card className="p-6 border-[#E2E8F0] max-w-lg" data-testid="sector-card">
          <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Sector de doctrina</Label>
          <p className="text-sm text-[#0E2A47]/60 mt-1 mb-3">Determina qué conocimiento sectorial usa el clasificador IA al analizar documentos.</p>
          <Select value={sector} onValueChange={setSector}>
            <SelectTrigger data-testid="sector-select" className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{SECTORES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="mt-4"><Button onClick={guardar} data-testid="sector-guardar" className="bg-[#0E2A47] hover:bg-[#163C65]"><Save size={15} className="mr-1" />Guardar</Button></div>
        </Card>
      )}
    </div>
  );
}
